import { useContext, useContextSelector } from 'use-context-selector';
import { AppAuthenticationContext } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication';
import { CustomerType } from '../../domain/UserInfo/entities/UserInfo';
import { UserInfoContext } from './UserInfoContext';

const useUserInfoState = () => {
  const context = useContext(UserInfoContext);

  if (context === undefined) {
    throw new Error('useUserInfoState must be used within UserInfoProvider');
  }

  return context;
};

const useUserInfo = () => {
  const context = useContextSelector(UserInfoContext, state => state.userInfo);

  if (context === undefined) {
    throw new Error('useUserInfo must be used within UserInfoProvider');
  }

  return context;
};

const useCustomerList = () => {
  const context = useContextSelector(UserInfoContext, state => state.customerList);

  if (context === undefined) {
    throw new Error('useCustomerCompaniesList must be used within UserInfoProvider');
  }

  return context;
};

const useCorporateCustomerId = () => {
  const customerList = useContextSelector(UserInfoContext, state => state.customerList);

  const customerId = useContextSelector(UserInfoContext, state => state.selectedCustomerId);

  if (customerList === undefined) {
    throw new Error('useCorporateCustomerId must be used within UserInfoProvider');
  }

  if (customerId === undefined) {
    throw new Error('useCorporateCustomerId must be used within UserInfoProvider');
  }

  const { customerType } = customerList?.find(customer => customer.customerId === customerId)!;

  return customerType === CustomerType.CORPORATE ? customerId : undefined;
};

const useCustomerType = () => {
  const customerList = useContextSelector(UserInfoContext, state => state.customerList);
  const customerId = useContextSelector(UserInfoContext, state => state.selectedCustomerId);

  if (customerList === undefined) {
    throw new Error('useCustomerType must be used within UserInfoProvider');
  }

  if (customerId === undefined) {
    throw new Error('useCustomerType must be used within UserInfoProvider');
  }

  const { customerType } = customerList?.find(customer => customer.customerId === customerId)!;

  return customerType;
};

const useIsCorporateUser = () => {
  const customerList = useContextSelector(UserInfoContext, state => state.customerList);
  const customerId = useContextSelector(UserInfoContext, state => state.selectedCustomerId);

  if (customerList === undefined) {
    throw new Error('useIsCorporateUser must be used within UserInfoProvider');
  }

  if (customerId === undefined) {
    throw new Error('useIsCorporateUser must be used within UserInfoProvider');
  }

  const { customerType } = customerList?.find(customer => customer.customerId === customerId)!;

  return customerType === CustomerType.CORPORATE;
};

const useSelectedCustomerId = () => {
  const context = useContextSelector(UserInfoContext, state => state.selectedCustomerId!);

  if (context === undefined) {
    throw new Error('useSelectedCustomerId must be used within UserInfoProvider');
  }

  return context;
};

const useSelectedCustomer = () => {
  const customerId = useContextSelector(UserInfoContext, state => state.selectedCustomerId!);
  const customerList = useContextSelector(UserInfoContext, state => state.customerList);

  if (customerList === undefined) {
    throw new Error('useIsCorporateUser must be used within UserInfoProvider');
  }

  if (customerId === undefined) {
    throw new Error('useIsCorporateUser must be used within UserInfoProvider');
  }

  return customerList?.find(customer => customer.customerId === customerId)!;
};

const useSetSelectedCustomerId = () => {
  const context = useContextSelector(UserInfoContext, state => state.onSelectCustomerId);

  if (context === undefined) {
    throw new Error('useSetSelectedCustomerId must be used within UserInfoProvider');
  }

  return context;
};

const useRefetchDashboardData = () => {
  const context = useContextSelector(UserInfoContext, state => state.refetchDashboardData);

  return context;
};

const useUserInfoIsUserAllowedAction = (
  requiredEntitlements?: string | string[],
  logic: 'OR' | 'AND' = 'OR',
): boolean => {
  const userRoleEntitlements = useContextSelector(UserInfoContext, state => state.entitlements);
  const normalisedUserRoleEntitlements = userRoleEntitlements
    ? userRoleEntitlements.map(userRoleEntitlement => userRoleEntitlement.name)
    : null;
  const accessControlService = useContextSelector(AppAuthenticationContext, state => state.accessControlService);

  if (accessControlService === undefined || !requiredEntitlements) {
    return true;
  }
  return accessControlService.isUserAllowedAction(requiredEntitlements, normalisedUserRoleEntitlements, logic);
};

const useUserInfoLoadingState = (loadingProperty: 'isLoadingEntitlementsData') => {
  const context = useContextSelector(UserInfoContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useAccountDetailsLoadingState must be used within UserInfoProvider');
  }

  return context[loadingProperty];
};

export {
  useUserInfoState,
  useUserInfo,
  useCustomerType,
  useIsCorporateUser,
  useCustomerList,
  useSelectedCustomerId,
  useCorporateCustomerId,
  useSetSelectedCustomerId,
  useRefetchDashboardData,
  useUserInfoIsUserAllowedAction,
  useUserInfoLoadingState,
  useSelectedCustomer,
};
